import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Heart, Users, Shield, Award, Lightbulb, HandHeart, Smile } from "lucide-react";

const values = [
  { icon: Heart, title: "Humanité", desc: "Placer l'être humain au cœur de chaque action, avec compassion et empathie." },
  { icon: Users, title: "Solidarité", desc: "S'unir pour agir ensemble, car la force réside dans le collectif." },
  { icon: Shield, title: "Intégrité", desc: "Agir avec transparence, honnêteté et responsabilité dans toutes nos démarches." },
  { icon: Award, title: "Excellence", desc: "Viser le meilleur dans chaque programme pour maximiser l'impact positif." },
  { icon: Lightbulb, title: "Innovation", desc: "Rechercher constamment des solutions créatives et durables aux défis sociaux." },
  { icon: HandHeart, title: "Dignité Humaine", desc: "Respecter et défendre la dignité de chaque individu, sans exception." },
  { icon: Smile, title: "Inclusion", desc: "Ouvrir nos actions à tous, sans discrimination d'origine, de genre ou de condition." },
];

export default function Values() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    /* @section: values */
    <section
      id="valeurs"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: "oklch(0.97 0.008 45)" }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/3 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-3">
            Ce qui nous guide
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Nos Valeurs Fondamentales
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Ces valeurs sont le socle de notre engagement et la boussole de chacune de nos actions.
          </p>
        </motion.div>

        {/* Values grid */}
        {/* @section: values-grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {values.map((value, i) => {
            const Icon = value.icon;
            return (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 28 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.08 * i }}
                className="group relative bg-card border border-border rounded-3xl p-6 hover:border-accent/40 hover:shadow-[0_8px_32px_oklch(0.72_0.11_45/0.12)] transition-all duration-300 hover:-translate-y-1"
              >
                {/* Top accent line on hover */}
                <div className="absolute inset-x-0 top-0 h-0.5 rounded-t-3xl bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                
                <div className="w-12 h-12 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center mb-4 group-hover:bg-accent/10 group-hover:border-accent/25 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-primary group-hover:text-accent transition-colors duration-300" />
                </div>
                <h3
                  className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-200"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {value.desc}
                </p>
              </motion.div>
            );
          })}

          {/* Last card — special motto card */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.08 * 7 }}
            className="group relative rounded-3xl p-6 overflow-hidden"
            style={{ background: "linear-gradient(135deg, oklch(0.28 0.09 240) 0%, oklch(0.2 0.07 240) 100%)" }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-accent/10 blur-2xl" />
            <div className="relative z-10">
              <Heart className="w-6 h-6 text-accent mb-4 fill-current" />
              <p
                className="text-white text-lg font-bold leading-snug mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Notre Devise
              </p>
              <p className="text-white/70 text-sm leading-relaxed italic">
                "Ensemble, révélons les talents, redonnons espoir et construisons un avenir meilleur."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
